import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, CircularProgress, Chip, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { InstitutionService } from '../../services/institution.service';
import { useToast } from '../../context/ToastContext';
import { IInstitution } from '../../interfaces/institution.interface';
import { ModuleKey } from '../dashboard/registry';
import { GATED_MODULE_KEYS, MODULE_META } from '../dashboard/registry/moduleMeta';

/**
 * SaaS Operator console: per-institution premium entitlement + gated-module provisioning.
 *
 * Deliberately cross-tenant — the SaaS Operator provisions on behalf of the SaaS product across
 * every institution (the backend restricts the list + write endpoints to SaaSOperator / SystemAdmin).
 * A "Manage" dialog per institution combines both writes (premium PATCH + enabled-modules PUT) into
 * one Save so the two entitlements are never left half-applied.
 */
export default function TenantProvisioning() {
    const toast = useToast();
    const [rows, setRows] = useState<IInstitution[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);

    const [managing, setManaging] = useState<IInstitution | null>(null);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [dialogPremium, setDialogPremium] = useState(false);
    const [dialogModules, setDialogModules] = useState<Set<ModuleKey>>(new Set());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await InstitutionService.getInstitutions(page, rowsPerPage);
            setRows(res.content ?? []);
            setTotal(res.totalElements ?? 0);
        } catch (err) {
            toast.showApiError(err, 'Failed to load institutions');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, toast]);

    useEffect(() => { load(); }, [load]);

    const openManage = async (institution: IInstitution) => {
        setManaging(institution);
        setDialogPremium(institution.premiumDashboardEnabled === true);
        setDialogLoading(true);
        try {
            const modules = await InstitutionService.getEnabledModules(institution.id);
            setDialogModules(new Set(modules.filter((m): m is ModuleKey => (GATED_MODULE_KEYS as string[]).includes(m))));
        } catch (err) {
            toast.showApiError(err, 'Failed to load enabled modules');
            setDialogModules(new Set());
        } finally {
            setDialogLoading(false);
        }
    };

    const toggleModule = (key: ModuleKey) => {
        setDialogModules(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const save = async () => {
        if (!managing) return;
        setSaving(true);
        try {
            await Promise.all([
                InstitutionService.setPremiumDashboard(managing.id, dialogPremium),
                InstitutionService.setEnabledModules(managing.id, Array.from(dialogModules)),
            ]);
            setRows(prev => prev.map(r => (r.id === managing.id ? { ...r, premiumDashboardEnabled: dialogPremium } : r)));
            toast.showSuccess(`Provisioning saved for ${managing.name}`);
            setManaging(null);
        } catch (err) {
            toast.showApiError(err, 'Failed to save provisioning');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WorkspacePremiumIcon color="primary" />
                <Typography variant="h5">Tenant Provisioning</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Unlock the business-owner dashboard and choose which modules are enabled, per institution.
            </Typography>

            <Paper>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Institution</TableCell>
                                        <TableCell>Code</TableCell>
                                        <TableCell align="center">Premium dashboard</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">No institutions found.</TableCell>
                                        </TableRow>
                                    ) : rows.map(inst => (
                                        <TableRow key={inst.id} hover>
                                            <TableCell>{inst.name}</TableCell>
                                            <TableCell>{inst.code}</TableCell>
                                            <TableCell align="center">
                                                {inst.premiumDashboardEnabled
                                                    ? <Chip size="small" color="success" label="Enabled" />
                                                    : <Chip size="small" variant="outlined" label="Disabled" />}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button size="small" onClick={() => openManage(inst)}>Manage</Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </>
                )}
            </Paper>

            <Dialog open={managing !== null} onClose={() => setManaging(null)} fullWidth maxWidth="xs" closeAfterTransition={false}>
                <DialogTitle>Manage {managing?.name}</DialogTitle>
                <DialogContent dividers>
                    {dialogLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
                    ) : (
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={dialogPremium} onChange={(e) => setDialogPremium(e.target.checked)} />}
                                label="Premium dashboard"
                            />
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Enabled modules</Typography>
                            {GATED_MODULE_KEYS.map(key => (
                                <FormControlLabel
                                    key={key}
                                    control={<Checkbox checked={dialogModules.has(key)} onChange={() => toggleModule(key)} />}
                                    label={MODULE_META[key].label}
                                />
                            ))}
                        </FormGroup>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManaging(null)} disabled={saving}>Cancel</Button>
                    <Button onClick={save} disabled={saving || dialogLoading} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
